import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PlatformBadge from '../common/PlatformBadge';

interface UpcomingPostItemProps {
  post: {
    id: string;
    title: string;
    scheduledAt: string; // ISO date string
    platforms: Array<'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'twitter'>;
  };
}

export default function UpcomingPostItem({ post }: UpcomingPostItemProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Format scheduled time (relative)
  const formatScheduledTime = (isoDate: string): string => {
    const date = new Date(isoDate);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });

    // Check if today
    if (date.toDateString() === now.toDateString()) {
      return `${t('dashboard.upcoming.today')} ${timeStr}`;
    }

    // Check if tomorrow
    if (date.toDateString() === tomorrow.toDateString()) {
      return `${t('dashboard.upcoming.tomorrow')} ${timeStr}`;
    }

    // Otherwise, show day name
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${dayName} ${timeStr}`;
  };

  // Truncate title (max 50 chars)
  const truncatedTitle = post.title.length > 50 ? `${post.title.substring(0, 50)}...` : post.title;

  return (
    <button
      onClick={() => navigate(`/dashboard/approve?postId=${post.id}`)}
      className="
        w-full flex items-center gap-3 p-3 rounded-lg border border-transparent
        hover:bg-gray-50 dark:hover:bg-gray-800
        hover:border-gray-300 dark:hover:border-gray-700
        transition-all duration-200 text-left group
      "
    >
      {/* Platform Badges */}
      <div className="flex gap-1 flex-shrink-0">
        {post.platforms.map((platform) => (
          <PlatformBadge key={platform} platform={platform} />
        ))}
      </div>

      {/* Post Title */}
      <p className="flex-1 text-sm text-gray-900 dark:text-gray-100 font-medium group-hover:underline">
        {truncatedTitle}
      </p>

      {/* Scheduled Time */}
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium flex-shrink-0">
        {formatScheduledTime(post.scheduledAt)}
      </span>
    </button>
  );
}
